import React, { forwardRef } from "react";
import { IconProps, useApiProps } from "../icon";
import { useIconColorState } from "./use-icon-color-state";

/**
 * 图标工厂：把每个图标文件里重复的 props 解构、颜色状态机、SVG 外壳收敛到这一处。
 * 图标文件只需提供 stem / componentName / defaultColors 与 render 函数。
 *
 * render 负责输出完整的 <svg> 根元素（含 xmlns/width/height/viewBox 等根属性），
 * 工厂只负责把注入属性、用户透传属性与根元素属性按序合并后重建根元素，
 * 合并顺序与优先级与原生成模板完全一致。
 */
export function createIcon(
  stem: string,
  componentName: string,
  defaultColors: string[],
  render: (colorList: string[]) => React.ReactElement
): React.ForwardRefExoticComponent<
  IconProps & React.RefAttributes<SVGSVGElement>
> {
  const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
    const {
      colors = [],
      className,
      id = "",
      hover = "",
      size,
      disabled = false,
      style,
      color,
      onMouseEnter,
      onMouseLeave,
      ...resultProps
    } = props;

    const { sizeStyle, cls } = useApiProps({
      className,
      initColor: defaultColors,
      initName: stem,
      colors,
      id,
      hover,
      size,
      style,
      disabled,
      color,
    });

    const { colorList, handleMouseEnter, handleMouseLeave } =
      useIconColorState({
        name: stem,
        colors,
        color,
        hover,
        defaultColors,
        onMouseEnter,
        onMouseLeave,
      });

    const root = render(colorList);

    return React.createElement(
      root.type as React.ElementType,
      {
        id: stem,
        style: sizeStyle,
        className: cls,
        ref,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        ...resultProps,
        ...root.props,
      } as any
    );
  });
  Icon.displayName = componentName;
  return Icon;
}
